import React, {useContext, useEffect, useRef, useState} from "react"
import Row from "@amzn/meridian/row"
import Column from "@amzn/meridian/column"
import {TableCell, TableRow} from "@amzn/meridian/table";
import {FormattedMessage, injectIntl} from "react-intl";
import {AdtaDeviceOverviewContext} from "./context/AdtaDeviceOverviewContext";
import {
    chainWalk,
    getAssetRefreshInterval,
    compressSorterConfig,
    sortComparator,
    getTestConfigKey,
} from "../../util/Util";
import Box from "@amzn/meridian/box";
import Button from "@amzn/meridian/button";
import Icon from "@amzn/meridian/icon";
import AddAdtaSorterConfigurationModal from "./modal/AddAdtaSorterConfigurationModal";
import arrowRightTokens from "@amzn/meridian-tokens/base/icon/arrow-right-large";
import Loader from "../../loader/Loader";
import DeleteAdtaSorterConfigurationModal from "./modal/DeleteAdtaSorterConfigurationModal";
import trashTokens from "@amzn/meridian-tokens/base/icon/trash";
import plusCircleTokens from "@amzn/meridian-tokens/base/icon/plus-circle";
import uploadTokens from "@amzn/meridian-tokens/base/icon/upload-small";
import AddAdtaSorterTestPackageModal from "./modal/AddAdtaSorterTestPackageModal";
import DeleteAdtaSorterTestPackageModal from "./modal/DeleteAdtaSorterTestPackageModal";
import UploadFileModal from "../../commonui/UploadFileModal";
import {SorterConfigCSVParser, TestConfigCSVParser} from "../../util/Parsers";
import {ASCENDING, CONFIG_TYPE, OPERATION_COLOR, SEARCH_ROW_COLOR} from "../../util/Constants";
import {DraftContext} from "../../draftSave/context/DraftSaveContext";
import ConfigurationTabHeader from "../../commonui/ConfigurationTabHeader";
import PaginatedTable from "../../commonui/PaginatedTable";
import Text from "@amzn/meridian/text";
import {getAssetVersions, getAssetByVersion} from "../../resources/AssetResource";
import RollbackModal from "../../rollback/RollbackModal";
import Input from "@amzn/meridian/input";
import {GlobalStateContext} from "../../global/context/GlobalStateContext";
import {getLocalizedMessage} from "../../util/Localization";
import {PermissionsContext} from "../../permissions/PermissionsContext";
import {ConditionalWrapping, tooltipWrapper} from "../../util/ConditionalWrapping";

const AdtaDeviceConfiguration = ({intl}) => {

    const {viewModel, dispatch} = useContext(AdtaDeviceOverviewContext);
    const {getDraftData, setDraftData, resetDraftData, getFilteredDraftData, isDataEdited} = useContext(DraftContext);
    const {isAuthorized} = useContext(PermissionsContext);
    const [openAddModal, setOpenAddModal] = useState(false)
    const [openDeleteModal, setOpenDeleteModal] = useState(false)
    const [openCSVModal, setOpenCSVModal] = useState(false)
    const [valueConfiguration, setValueConfiguration] = useState("")
    const [selectedMac, setSelectedMac] = useState("")
    const [showRollbackModal, setShowRollbackModal] = useState(false)
    const [isVersionsLoading, setIsVersionsLoading] = useState(false);
    const [selectedVersion, setSelectedVersion] = useState("");
    const [rollBackVersions, setRollbackVersions] = useState([]);
    const [sorterTestPackageSortColumn, setSorterTestPackageSortColumn] = useState("scannables");
    const [sorterTestPackageSortOrder, setSorterTestPackageSortOrder] = useState(ASCENDING);
    const [sorterDestinationSortColumn, setSorterDestinationSortColumn] = useState("bayId");
    const [sorterDestinationSortOrder, setSorterDestinationSortOrder] = useState(ASCENDING);
    const tableEndRef = useRef(null);
    const [scannablesSearchInput, setScannablesSearchInput] = useState('');
    const [sortZoneSearchInput, setSortZoneSearchInput] = useState('');
    const { showCustomAlert } = useContext(GlobalStateContext);

    const PAGE_CONFIGURATION_KEYS = [CONFIG_TYPE.SORTER_TEST_PACKAGE, CONFIG_TYPE.SORTER_DESTINATION]

    const searchDataForTestConfigTable = () => {
        let data = getDraftData({
            stateKey: CONFIG_TYPE.SORTER_TEST_PACKAGE,
            subKey: selectedMac
        })

        data = data.filter(item => getTestConfigKey(item.scannables)
                .toLowerCase()
                .includes(scannablesSearchInput.toLowerCase()))
            .filter(item => item.sortZone
                .toLowerCase()
                .includes(sortZoneSearchInput.toLowerCase())
            )
        data = data.sort((a, b)=>
            sortTestConfigColumns[sorterTestPackageSortColumn](a, b, sorterTestPackageSortOrder === ASCENDING)
        )
        return data;
    }

    const getModalContent = async () => {
        setIsVersionsLoading(true);
        let versionsData = await getAssetVersions(selectedMac);
        setRollbackVersions(versionsData.data.assetVersionDetailsList);
        setIsVersionsLoading(false);
    }

    const loadRollbackDataToDraft = async () => {
        let rollbackData = await getAssetByVersion(selectedMac, selectedVersion);
        const sortTestPackageData = chainWalk(() => JSON.parse(rollbackData.data.asset.assetDetails.assetAttributes.sorterTestPackage), []);
        const sorterConfigData = chainWalk(() => JSON.parse(rollbackData.data.asset.assetDetails.assetAttributes.sorterConfig), []);
        if(sortTestPackageData.length) {
            setDraftData({
                data: sortTestPackageData,
                stateKey: CONFIG_TYPE.SORTER_TEST_PACKAGE,
                subKey: selectedMac,
                allRowsAdded: true,
                override: true
            });
        }
        if(sorterConfigData.length) {
            setDraftData({
                data: sorterConfigData,
                stateKey: CONFIG_TYPE.SORTER_DESTINATION,
                subKey: selectedMac,
                allRowsAdded: true,
                override: true
            });
        }
    }

    useEffect(() => {
        if (!openAddModal && !openDeleteModal) {
            getData()
            const interval = setInterval(() => {
                if (!document.hidden) {
                    getData();
                }
            }, getAssetRefreshInterval());
            return () => clearInterval(interval);
        }
    }, [openAddModal, openDeleteModal]);

    useEffect(() => {
        setDraftData({
           data: chainWalk(() => viewModel.sorterTestPackageConfig[selectedMac], []),
           stateKey: CONFIG_TYPE.SORTER_TEST_PACKAGE,
           subKey: selectedMac,
        })
        setDraftData({
            data: chainWalk(() => viewModel.sorterConfig[selectedMac], []),
            stateKey: CONFIG_TYPE.SORTER_DESTINATION,
            subKey: selectedMac,
        })
    }, [selectedMac]);

    const getSortedDeviceList = () => {
        const sortedDeviceList = Object.values(viewModel.adtaDeviceInformation);
        sortedDeviceList && sortedDeviceList.sort((a, b) => {
            if (a.name < b.name) {
                return -1
            } else if (b.name > a.name) {
                return 1
            }
            return 0
        })
        return sortedDeviceList
    }

    const getData = () => {
        if(isDataEdited(PAGE_CONFIGURATION_KEYS)) return;
        setOpenAddModal(false)
        setOpenDeleteModal(false)
        dispatch({type: 'LOAD_VIEW', data: {dispatcher: dispatch}});
    }
    const scrollToBottom = () => {
        tableEndRef.current.scrollIntoView({behavior: "smooth"})
    }

    const updateDestinationConfigDraftData = (sorterConfig) => {
        const compressedSorterConfig = compressSorterConfig(sorterConfig);
        setDraftData({
            data: chainWalk(() => compressedSorterConfig, []),
            stateKey: CONFIG_TYPE.SORTER_DESTINATION,
            subKey: selectedMac,
            allRowsAdded: true,
            override: true
        })
    }

    const updateTestConfigDraftData = (sorterTestPackage) => {
        setDraftData({
            data: chainWalk(() => sorterTestPackage, []),
            stateKey: CONFIG_TYPE.SORTER_TEST_PACKAGE,
            subKey: selectedMac,
            allRowsAdded: true,
            override: true
        })
    }

    const deployDraftADTAAsset = () => {
        const sorterDestinationList = getFilteredDraftData({
            stateKey: CONFIG_TYPE.SORTER_DESTINATION,
            subKey: selectedMac
        })
        const testPackageList = getFilteredDraftData({
            stateKey: CONFIG_TYPE.SORTER_TEST_PACKAGE,
            subKey: selectedMac
        })

        dispatch({
            type: 'UPDATE_ADTA_CONFIGURATION',
            data: {
                dispatcher: dispatch,
                showCustomAlert: showCustomAlert,
                configurations: {
                    sorterTestPackage: testPackageList,
                    sorterConfig: sorterDestinationList
                },
                mac: selectedMac
            }
        });

        setDraftData({
            data: sorterDestinationList,
            stateKey: CONFIG_TYPE.SORTER_DESTINATION,
            subKey: selectedMac,
            override: true
        })
        setDraftData({
            data: testPackageList,
            stateKey: CONFIG_TYPE.SORTER_TEST_PACKAGE,
            subKey: selectedMac,
            override: true
        })
    }

    function onClickUploadCSV() {
        setOpenCSVModal(true)
    }

    function onClickAddSingle() {
        setOpenAddModal(true)
    }

    function onClickDeleteConfiguration() {
        setOpenDeleteModal(true)
    }

    const getDataStringFromConfig = (config) => {
        return ['miss', 'jackpot', 'recirculation', 'problem_solve'].includes(config.installation.type) ?
            (config.fingerName ? config.fingerName : "") : config.installation.locations.join(' ');
    }

    const renderSorterConfigTableRow = (config, index) => {
        const dataString = getDataStringFromConfig(config)
        const prevDataString = chainWalk(() => getDataStringFromConfig(config.prev), null)
        return (
            <TableRow highlightOnHover={true} key={index} backgroundColor={OPERATION_COLOR[config.operation]}>
                <TableCell alignmentVertical="top">
                    <Column spacingInset="200 none none">{config.bayId}</Column>
                </TableCell>
                <TableCell>
                    {config.installation.type}
                </TableCell>
                <TableCell>
                    <Row>
                        {prevDataString && <Text> {prevDataString} </Text>}
                        {prevDataString && <Icon tokens={arrowRightTokens}/>}
                        <Text> {dataString} </Text>
                    </Row>
                </TableCell>
            </TableRow>
        )
    }

    const sortDestinationConfigColumns = {
        bayId: (a, b, ascending) => {
            return ascending ? sortComparator(a.bayId, b.bayId) : -sortComparator(a.bayId, b.bayId);
        },
        installationType: (a, b, ascending) => {
            return ascending ? sortComparator(a.installation.type, b.installation.type) : -sortComparator(a.installation.type, b.installation.type);
        },
        location: (a, b, ascending) => {
            return ascending ? sortComparator(getDataStringFromConfig(a), getDataStringFromConfig(b)) : -sortComparator(getDataStringFromConfig(a), getDataStringFromConfig(b));
        }
    }

    const sortTestConfigColumns = {
        scannables: (a, b, ascending) => {
            let scannableFirst = Array.isArray(a.scannables) ? a.scannables.join(' ') : a.scannables;
            let scannableSecond = Array.isArray(b.scannables) ? b.scannables.join(' ') : b.scannables;
            return ascending ? sortComparator(scannableFirst, scannableSecond) : -sortComparator(scannableFirst, scannableSecond);
        },
        sortZone: (a, b, ascending) => {
            return ascending ? sortComparator(a.sortZone, b.sortZone) : -sortComparator(a.sortZone, b.sortZone);
        }
    }

    const renderSorterConfigTableHeader = () => {
        return (<TableRow highlightOnHover={false}>
            <TableCell sortColumn="bayId">
                {getLocalizedMessage({intl, stringId: "destination.id"})}
            </TableCell>
            <TableCell sortColumn="installationType">
                <FormattedMessage id="bay.installationtype" defaultMessage="Installation Type"/>
            </TableCell>
            <TableCell sortColumn="location">
                <FormattedMessage id="bay.location" defaultMessage="Locations"/>
            </TableCell>
        </TableRow>)
    }

    const getHeaderProps = () => {
        return {
            selectedMac,
            setSelectedMac,
            valueConfiguration,
            setValueConfiguration,
            isSorterConfig: valueConfiguration === CONFIG_TYPE.SORTER_DESTINATION, isAuthorized, intl,
            defaultSelection: "",
            resetDraftData,
            deployDraft: deployDraftADTAAsset,
            scrollToBottom,
            sortedDeviceList: getSortedDeviceList(),
            configurationKeys: PAGE_CONFIGURATION_KEYS,
            refreshDeps: [viewModel.timestamp],
            isDeployDisabled: !isDataEdited(PAGE_CONFIGURATION_KEYS),
            isRollbackDisabled: !selectedMac || isDataEdited(PAGE_CONFIGURATION_KEYS),
            rollbackClickHandler: () => {getModalContent(); setShowRollbackModal(true)}
        }
    }

    const renderTestConfigTableHeader = () => {
        return (
            <TableRow highlightOnHover={false}>
                <TableCell>
                    <FormattedMessage id="bay.index" defaultMessage="Index"/>
                </TableCell>
                <TableCell sortColumn="scannables">
                    <FormattedMessage id="bay.scannables" defaultMessage="Scannables"/>
                </TableCell>
                <TableCell sortColumn="sortZone">
                    <FormattedMessage id="bay.sortzone" defaultMessage="Sort Zone"/>
                </TableCell>
            </TableRow>
        )
    }

    const renderTestConfigTableRow = (config, serialNumber) => {
        const dataString = config.sortZone
        const prevDataString = chainWalk(() => config.prev.sortZone, null)
        return  (
            <TableRow highlightOnHover={true} backgroundColor={OPERATION_COLOR[config.operation]}>
                <TableCell>
                    {serialNumber}
                </TableCell>
                <TableCell>
                    {Array.isArray(config.scannables) ? config.scannables.join(' ') : config.scannables}
                </TableCell>
                <TableCell>
                    {prevDataString && <Text> {prevDataString} </Text>}
                    {prevDataString && <Icon tokens={arrowRightTokens}/>}
                    <Text> {dataString} </Text>
                </TableCell>
            </TableRow>
        )
    }

    const renderSearchRow = () => {
        return (
            <TableRow backgroundColor={SEARCH_ROW_COLOR}>
                <TableCell/>
                <TableCell>
                    <Input
                        className="input-search-class"
                        value={scannablesSearchInput}
                        onChange={setScannablesSearchInput}
                        type="text"
                        placeholder="Search"
                        width="70%"
                        size="small"/>
                </TableCell>
                <TableCell>
                    <Input
                        className="input-search-class"
                        value={sortZoneSearchInput}
                        onChange={setSortZoneSearchInput}
                        type="text"
                        placeholder="Search"
                        width="70%"
                        size="small"/>
                </TableCell>
                <TableCell/>
            </TableRow>
        )
    }

    if (viewModel.loadingOverview) {
        return (<Loader/>)
    } else {
        return (
            <Column>
                <ConfigurationTabHeader {...getHeaderProps()}/>
                <RollbackModal
                    showRollbackModal={showRollbackModal}
                    setShowRollbackModal={setShowRollbackModal}
                    isVersionsLoading={isVersionsLoading}
                    selectedVersion={selectedVersion}
                    setSelectedVersion={setSelectedVersion}
                    rollBackVersions={rollBackVersions}
                    loadRollbackDataToDraft={loadRollbackDataToDraft}
                    intl={intl}
                />
                {valueConfiguration === CONFIG_TYPE.SORTER_DESTINATION && selectedMac &&
                <Column>
                    <PaginatedTable
                        data={getDraftData({stateKey: CONFIG_TYPE.SORTER_DESTINATION, subKey: selectedMac}).sort((a, b) => {
                            return sortDestinationConfigColumns[sorterDestinationSortColumn](a, b, sorterDestinationSortOrder === ASCENDING);
                        })}
                        renderRow={renderSorterConfigTableRow}
                        renderHeader={renderSorterConfigTableHeader}
                        sortColumn={sorterDestinationSortColumn}
                        sortDirection={sorterDestinationSortOrder}
                        onSort={({sortColumn, sortDirection}) => {
                            setSorterDestinationSortColumn(sortColumn);
                            setSorterDestinationSortOrder(sortDirection);
                        }}
                    />
                    <Row width="100%" widths={["100", "100", "fill", "100"]}>
                        <ConditionalWrapping
                            condition={!isAuthorized}
                            wrapper={tooltipWrapper}
                            args={{
                                title: getLocalizedMessage({intl, stringId: "tooltip.isAuthorized"}),
                                position: "top"
                            }}
                            child={<Button onClick={onClickAddSingle} type="primary" size="small"
                                           disabled={!isAuthorized}
                            >
                                <Icon tokens={plusCircleTokens}/>
                                <FormattedMessage id="sorterconfig.addupdate"
                                                  defaultMessage="Add/Update Sorter Destination Configuration"/>
                            </Button>}
                        />
                        <ConditionalWrapping
                            condition={!isAuthorized}
                            wrapper={tooltipWrapper}
                            args={{
                                title: getLocalizedMessage({intl, stringId: "tooltip.isAuthorized"}),
                                position: "top"
                            }}
                            child={<Button onClick={onClickUploadCSV} type="primary" size="small"
                                           disabled={!isAuthorized}
                            >
                                <Icon tokens={uploadTokens}/>
                                <FormattedMessage id="sorterconfig.uploadCSV"
                                                  defaultMessage="Upload Sorter Destination Configuration CSV"/>
                            </Button>}
                        />
                        <Box></Box>
                        <ConditionalWrapping
                            condition={!isAuthorized}
                            wrapper={tooltipWrapper}
                            args={{
                                title: getLocalizedMessage({intl, stringId: "tooltip.isAuthorized"}),
                                position: "top"
                            }}
                            child={<Button onClick={onClickDeleteConfiguration} size="small" type="tertiary"
                                           disabled={!isAuthorized}
                            >
                                <Icon tokens={trashTokens}/>
                                <FormattedMessage id="config.delete" defaultMessage="Delete Configuration"/>
                            </Button>}
                        />
                    </Row>
                    <UploadFileModal mac={selectedMac}
                                     open={openCSVModal} setOpen={setOpenCSVModal}
                                     parser={SorterConfigCSVParser} updateConfig={updateDestinationConfigDraftData}/>
                    <AddAdtaSorterConfigurationModal mac={selectedMac}
                                                     open={openAddModal} setOpen={setOpenAddModal}/>
                    <DeleteAdtaSorterConfigurationModal mac={selectedMac}
                                                        open={openDeleteModal} setOpen={setOpenDeleteModal}/>
                    <div ref={tableEndRef}/>
                </Column>}
                {valueConfiguration === CONFIG_TYPE.SORTER_TEST_PACKAGE && selectedMac &&
                <Column>
                    <PaginatedTable
                        data={searchDataForTestConfigTable()}
                        renderRow={renderTestConfigTableRow}
                        renderHeader={renderTestConfigTableHeader}
                        renderSearchRow={renderSearchRow}
                        sortColumn={sorterTestPackageSortColumn}
                        sortDirection={sorterTestPackageSortOrder}
                        onSort={({sortColumn, sortDirection}) => {
                            setSorterTestPackageSortColumn(sortColumn);
                            setSorterTestPackageSortOrder(sortDirection);
                        }}
                    />
                    <Row width="100%" widths={["100", "100", "fill", "100"]}>
                        <Button onClick={onClickAddSingle}
                                type="primary"
                                size="small">
                            <Icon tokens={plusCircleTokens}/>
                            <FormattedMessage id="sortertestconfig.addupdatetest"
                                              defaultMessage="Add/Update Sorter Test Package Configuration"/>
                        </Button>
                        <Button onClick={onClickUploadCSV}
                                type="primary"
                                size="small">
                            <Icon tokens={uploadTokens}/>
                            <FormattedMessage id="sortertestconfig.uploadCSV"
                                              defaultMessage="Upload Sorter Test Package Configuration CSV"/>
                        </Button>
                        <Box></Box>
                        <Button onClick={onClickDeleteConfiguration}
                                size="small" type="tertiary">
                            <Icon tokens={trashTokens}/>
                            <FormattedMessage id="config.deletetest" defaultMessage="Delete Sorter Test Package Configuration"/>
                        </Button>
                    </Row>
                    <UploadFileModal mac={selectedMac}
                                     open={openCSVModal} setOpen={setOpenCSVModal}
                                     parser={TestConfigCSVParser} updateConfig={updateTestConfigDraftData}/>
                    <AddAdtaSorterTestPackageModal mac={selectedMac}
                                                   open={openAddModal} setOpen={setOpenAddModal}/>
                    <DeleteAdtaSorterTestPackageModal mac={selectedMac}
                                                      open={openDeleteModal} setOpen={setOpenDeleteModal}/>
                    <div ref={tableEndRef}/>
                </Column>}
            </Column>
        );
    }
}

export default injectIntl(AdtaDeviceConfiguration)



// WEBPACK FOOTER //
// ./src/components/devicemanagement/adtadevice/AdtaDeviceConfiguration.js